import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const teamMembers = [
  {
    name: "CHITRA KATYAL",
    role: "Founder & Creative Head",
    desc: "Founder & Creative Head of Meraki Solutions. With a background spanning media, operations, production, design, and digital marketing, she approaches every brand like a living narrative.",
    image: "/images/chitra/chitra2.png",
    portfolioUrl: "/chitra"
  },
  {
    name: "PAWAN KATYAL",
    role: "Co-Founder & Business Head",
    desc: "Co-Founder & Business Head of Meraki Solutions. With 25+ years in management, marketing, sales, media and operations, he approaches business design like a motion picture narrative.",
    image: "/pawan.png",
    portfolioUrl: "/pawan"
  },
  {
    name: "SARAH CHEN",
    role: "Technical Architect",
    desc: "The engineer behind the impossible. Sarah transforms complex visions into high-performance digital reality.",
    image: "/images/chitra/chitra1.png",
    portfolioUrl: "/chitra"
  }
];

const TeamGrid = () => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    return smoothProgress.onChange((v) => {
      const index = Math.min(
        teamMembers.length - 1,
        Math.floor(v * teamMembers.length)
      );
      setActiveIndex(index);
    });
  }, [smoothProgress]);

  return (
    <>
      {/* Desktop Version - Sticky Switch */}
      <section id="collective" ref={containerRef} className="team-sticky-showcase hidden lg:block">
        <div className="sticky-container">
          <div className="container">
            <div className="grid grid-cols-12 gap-8 items-center">

              {/* Left: Fixed Info */}
              <div className="col-span-12 lg:col-span-5">
                <MemberKicker index="02" label="THE COLLECTIVE" />
                <h2 className="showcase-title">
                  THE SOULS <br />
                  <span className="italic gold-text">BEHIND THE CRAFT</span>
                </h2>

                <div className="member-navigation mt-12 space-y-4">
                  {teamMembers.map((member, i) => (
                    <button
                      key={i}
                      className={`member-nav-item ${activeIndex === i ? "active" : ""}`}
                      onClick={() => {
                        const target = (i / teamMembers.length) * (containerRef.current?.scrollHeight || 0);
                        window.scrollTo({ top: target + containerRef.current?.offsetTop, behavior: "smooth" });
                      }}
                    >
                      <span className="nav-num">0{i + 1}</span>
                      <span className="nav-name">{member.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Dynamic Cards */}
              <div className="col-span-12 lg:col-span-7 relative min-h-full flex items-center justify-end lg:pl-16">
                <AnimatePresence mode="wait">
                  <MemberCard
                    key={activeIndex}
                    member={teamMembers[activeIndex]}
                  />
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Version - Premium Stack */}
      <section className="lg:hidden bg-[#0d0c0b] py-24 px-4">
        <div className="container">
          <div className="mb-16">
            <MemberKicker index="02" label="THE COLLECTIVE" />
            <h2 className="text-4xl font-extrabold leading-none text-white">
              THE SOULS <br />
              <span className="italic gold-text">BEHIND THE CRAFT</span>
            </h2>
          </div>

          <div className="space-y-12">
            {teamMembers.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <MemberCard member={member} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const MemberCard = ({ member }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="member-card w-full"
    >
      <div className="premium-member-card group">
        <div className="card-image-wrapper">
          <img src={member.image} alt={member.name} className="card-img" />
          <div className="card-overlay">
            <button
              onClick={() => navigate(member.portfolioUrl)}
              className="card-visit-btn"
            >
              EXPLORE PORTFOLIO <ArrowUpRight size={18} />
            </button>
          </div>
        </div>
        <div className="card-details">
          <h4 className="card-name uppercase tracking-widest">{member.name}</h4>
          <p className="card-role gold-text italic">{member.role}</p>
          <p className="card-bio mt-4 text-white/60 leading-relaxed text-sm">{member.desc}</p>
        </div>
      </div>
    </motion.div>
  );
};

const MemberKicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--accentColor)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-white/40 uppercase">{label}</span>
  </div>
);

export default TeamGrid;
